import { humanizeToolName, isNavigationTool } from '../../mcp/toolCallDisplay';
import {
  type ChatFeedEntry,
  type ClientChatResponse,
  isToolTrail,
  type McpCallRecord,
  type ToolApprovalRequest,
  ToolOutcome,
  type ToolOutcomeRecord,
  type ToolTrailEntry,
  type ToolTrailRow,
  ToolTrailStatus,
} from '../../types/ClientMessage';
import { type ChatState, selectActiveApprovalCalls, selectActiveCall, ToolCallStatus } from './chatReducer';

/**
 * The chat feed as it is rendered: the persisted history minus the entries that only
 * exist for the LLM, with every tool call of a request folded into one tool trail card.
 *
 * A request's tools may span several backend turns (calls → results → more calls). They
 * all land in the same card, placed where the first call was made, so the whole sequence
 * reads as one checklist above the final answer. A row's status comes from the outcome
 * recorded on the turn's results once they exist, and from the live tool loop until then.
 */

const OUTCOME_STATUS: Record<ToolOutcome, ToolTrailStatus> = {
  [ToolOutcome.Done]: ToolTrailStatus.Done,
  [ToolOutcome.Failed]: ToolTrailStatus.Failed,
  [ToolOutcome.Denied]: ToolTrailStatus.Denied,
  [ToolOutcome.Skipped]: ToolTrailStatus.Skipped,
};

const isShownInFeed = (msg: ClientChatResponse) =>
  !(msg.role === 'user' && typeof msg.content !== 'string') &&
  msg.role !== 'tool' &&
  msg.mcpKind !== 'mcp_call' &&
  !msg.hidden &&
  msg.mcpKind !== 'background_handoff';

const rowLabel = (call: McpCallRecord) => call.explanation || humanizeToolName(call.name);

/** Several calls of one tool, explained alike, read as one step: a batch shows once. */
const repeats = (previous: ToolTrailRow | undefined, row: ToolTrailRow) =>
  previous !== undefined &&
  previous.name === row.name &&
  previous.label === row.label &&
  previous.status === row.status &&
  previous.approvedByUser === row.approvedByUser;

type Span = { calls: McpCallRecord[]; outcomes: Record<string, ToolOutcomeRecord>; trail: ToolTrailEntry };

const outcomeOf = (span: Span, state: ChatState, id: string | number): ToolOutcomeRecord | undefined =>
  span.outcomes[String(id)] ?? state.outbox.find((turn) => turn.id === id);

const buildRows = (span: Span, state: ChatState): Pick<ToolTrailEntry, 'rows' | 'approval'> => {
  const active = selectActiveCall(state);
  const approvalCalls = selectActiveApprovalCalls(state);
  const rows: ToolTrailRow[] = [];
  let approval: ToolApprovalRequest | undefined;

  for (const call of span.calls) {
    const base = { id: call.id, name: call.name, label: rowLabel(call), isNavigation: isNavigationTool(call.name) };
    const settled = outcomeOf(span, state, call.id);
    if (settled) {
      rows.push({ ...base, status: OUTCOME_STATUS[settled.outcome], approvedByUser: settled.approvedByUser });
      continue;
    }
    // Calls queued behind the active one are not shown until they are reached.
    if (!active || active.id !== call.id) continue;

    if (active.status === ToolCallStatus.AwaitingAuthorization) {
      const covered = span.calls.filter((c) => approvalCalls.some((a) => a.id === c.id));
      approval = {
        toolName: call.name,
        explanations: [...new Set(covered.flatMap((c) => (c.explanation ? [c.explanation] : [])))],
        resources: [...new Set(covered.flatMap((c) => c.resources))],
        callCount: covered.length,
      };
      rows.push({ ...base, status: ToolTrailStatus.AwaitingApproval, approvedByUser: false });
    } else {
      rows.push({ ...base, status: ToolTrailStatus.Running, approvedByUser: call.requiresApproval });
    }
  }
  return { rows: rows.filter((row, i) => !repeats(rows[i - 1], row)), approval };
};

export const isTrailLive = (entry: ChatFeedEntry | undefined): boolean =>
  isToolTrail(entry) &&
  entry.rows.some((row) => row.status === ToolTrailStatus.AwaitingApproval || row.status === ToolTrailStatus.Running);

export const buildChatFeed = (messages: ClientChatResponse[], state: ChatState): ChatFeedEntry[] => {
  const feed: ChatFeedEntry[] = [];
  let span: Span | null = null;

  const closeSpan = () => {
    if (!span) return;
    const built = buildRows(span, state);
    if (built.rows.length > 0) Object.assign(span.trail, built);
    else feed.splice(feed.indexOf(span.trail), 1);
    span = null;
  };

  for (const msg of messages) {
    if (msg.role === 'user') closeSpan();

    if (msg.mcpKind === 'mcp_call' && msg.mcpCalls?.length) {
      if (!span) {
        const trail: ToolTrailEntry = { kind: 'tool_trail', timestamp: msg.timestamp, rows: [] };
        feed.push(trail);
        span = { calls: [], outcomes: {}, trail };
      }
      span.calls.push(...msg.mcpCalls);
    } else if (msg.mcpKind === 'mcp_response' && msg.mcpOutcomes && span) {
      Object.assign(span.outcomes, msg.mcpOutcomes);
    }

    if (isShownInFeed(msg)) feed.push(msg);
  }
  closeSpan();
  return feed;
};
